<template>
    <div>
        <div class="row q-gutter-md q-mb-md items-end">
            <div class="col">
                <GltnInputNumber v-model="ancho" label="Ancho (m)" />
            </div>
            <div class="col">
                <GltnInputNumber v-model="largo" label="Largo (m)" />
            </div>
            <div class="col">
                <GltnInputNumber v-model="alto" label="Alto (m)" />
            </div>
        </div>

        <div style="height: 450px; border-radius: 8px; overflow: hidden;">
            <TresCanvas clear-color="#1a1a2e">
                <TresPerspectiveCamera :position="[11, 9, 11]" :fov="50" :look-at="[0, 0, 0]" />
                <CamaraOrbitControls />
                <TresAmbientLight :intensity="0.7" />
                <TresDirectionalLight :position="[8, 10, 8]" :intensity="1.2" />

                <!-- Cara semi-transparente -->
                <TresMesh>
                    <TresBoxGeometry :args="boxArgs" />
                    <TresMeshStandardMaterial
                        color="#90CAF9"
                        :transparent="true"
                        :opacity="0.15"
                        :side="doubleSide"
                    />
                </TresMesh>

                <!-- Aristas del cubo -->
                <primitive v-if="edgesLines" :object="edgesLines" />

                <!-- Etiquetas de dimensiones -->
                <primitive :object="anchoSprite" />
                <primitive :object="altoSprite" />
                <primitive :object="largoSprite" />
            </TresCanvas>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, shallowRef, watchEffect, onUnmounted, defineComponent, watch } from 'vue';
import { TresCanvas, useTres, useLoop } from '@tresjs/core';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import GltnInputNumber from '@/components/form/GltnInputNumber.vue';

// ─── Dimensiones ─────────────────────────────────────────────────────────────
const ancho = ref(3);   // X (width)
const largo = ref(5);   // Z (depth)
const alto = ref(2.5);  // Y (height)

const doubleSide = THREE.DoubleSide;

// ─── BoxGeometry args: [width (X), height (Y), depth (Z)] ────────────────────
const boxArgs = computed(() => [
    Math.max(Number(ancho.value) || 0.1, 0.1),
    Math.max(Number(alto.value) || 0.1, 0.1),
    Math.max(Number(largo.value) || 0.1, 0.1),
]);

// ─── Aristas reactivas con cleanup de memoria ─────────────────────────────────
const edgesLines = shallowRef(null);

watchEffect((onCleanup) => {
    const [bw, bh, bd] = boxArgs.value;
    const geo = new THREE.EdgesGeometry(new THREE.BoxGeometry(bw, bh, bd));
    const mat = new THREE.LineBasicMaterial({ color: 0x42A5F5 });
    const lines = new THREE.LineSegments(geo, mat);
    edgesLines.value = lines;

    onCleanup(() => {
        geo.dispose();
        mat.dispose();
    });
});

// ─── Sprites de texto ─────────────────────────────────────────────────────────
function drawSpriteCanvas(canvas, text) {
    const ctx = canvas.getContext('2d');
    const cw = canvas.width;
    const ch = canvas.height;
    ctx.clearRect(0, 0, cw, ch);

    const r = 10;
    ctx.fillStyle = 'rgba(10, 20, 60, 0.85)';
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.lineTo(cw - r, 0);
    ctx.arcTo(cw, 0, cw, r, r);
    ctx.lineTo(cw, ch - r);
    ctx.arcTo(cw, ch, cw - r, ch, r);
    ctx.lineTo(r, ch);
    ctx.arcTo(0, ch, 0, ch - r, r);
    ctx.lineTo(0, r);
    ctx.arcTo(0, 0, r, 0, r);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = 'rgba(66, 165, 245, 0.9)';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 26px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, cw / 2, ch / 2);
}

function makeSprite(text) {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    drawSpriteCanvas(canvas, text);
    const texture = new THREE.CanvasTexture(canvas);
    const spriteMat = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
    const sprite = new THREE.Sprite(spriteMat);
    sprite.scale.set(1.2, 0.3, 1);
    return sprite;
}

function updateSprite(sprite, text) {
    drawSpriteCanvas(sprite.material.map.image, text);
    sprite.material.map.needsUpdate = true;
}

const anchoSprite = shallowRef(makeSprite(`Ancho: ${ancho.value.toFixed(2)} m`));
const altoSprite  = shallowRef(makeSprite(`Alto: ${alto.value.toFixed(2)} m`));
const largoSprite = shallowRef(makeSprite(`Largo: ${largo.value.toFixed(2)} m`));

watchEffect(() => {
    const dimA = Math.max(Number(ancho.value) || 0.1, 0.1);
    const dimL = Math.max(Number(largo.value) || 0.1, 0.1);
    const dimH = Math.max(Number(alto.value) || 0.1, 0.1);
    const maxDim = Math.max(dimA, dimL, dimH);
    const scale = Math.max(maxDim * 0.22, 0.8);
    const vOffset = scale * 0.18;

    // Ancho (X): etiqueta en el borde inferior-frontal, centrada en X
    anchoSprite.value.position.set(0, -dimH / 2 - vOffset, dimL / 2 + 0.05);
    anchoSprite.value.scale.set(scale, scale * 0.25, 1);
    updateSprite(anchoSprite.value, `Ancho: ${dimA.toFixed(2)} m`);

    // Alto (Y): etiqueta en el borde izquierdo-frontal, centrada en Y
    altoSprite.value.position.set(-dimA / 2 - scale * 0.55, 0, dimL / 2 + 0.05);
    altoSprite.value.scale.set(scale, scale * 0.25, 1);
    updateSprite(altoSprite.value, `Alto: ${dimH.toFixed(2)} m`);

    // Largo (Z): etiqueta en el borde derecho-inferior, centrada en Z
    largoSprite.value.position.set(dimA / 2 + 0.05, -dimH / 2 - vOffset, 0);
    largoSprite.value.scale.set(scale, scale * 0.25, 1);
    updateSprite(largoSprite.value, `Largo: ${dimL.toFixed(2)} m`);
});

// ─── OrbitControls – componente hijo de TresCanvas ───────────────────────────
const CamaraOrbitControls = defineComponent({
    name: 'CamaraOrbitControls',
    setup() {
        const { renderer, camera } = useTres();
        const { onBeforeRender } = useLoop();
        let controls = null;

        watch(camera, (cam) => {
            if (cam && !controls) {
                controls = new OrbitControls(cam, renderer.domElement);
                controls.enableDamping = true;
                controls.dampingFactor = 0.05;
                controls.minDistance = 0.5;
                controls.maxDistance = 100;
                controls.target.set(0, 0, 0);
            }
        }, { immediate: true });

        onBeforeRender(() => {
            controls?.update();
        });

        onUnmounted(() => {
            controls?.dispose();
        });

        return () => null;
    },
});
</script>

<style lang="scss" scoped>
</style>

